import { formatPercentInput } from '@/features/quotes/schemas/commercialization-percent.schema';
import {
  Field,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Text,
} from '@noorden-insurtech/alba-design-system';

type Props = {
  brokerageValue: string;
  agencingValue: string;
  onBrokerageChange: (value: string) => void;
  onAgencingChange: (value: string) => void;
  /** Erro da corretagem (exibido sob o campo). */
  brokerageError?: string;
  /** Erro do agenciamento (exibido sob o campo). */
  agencingError?: string;
  /** Erro do teto da soma (exibido abaixo dos dois campos; marca ambos). */
  limitError?: string;
  disabled?: boolean;
  /**
   * Foca a corretagem ao montar. Opt-in em vez de sempre-ligado para que um
   * consumidor futuro não roube o foco sem querer; PME e Capital Global ligam.
   */
  autoFocusBrokerage?: boolean;
};

/**
 * Campos de comercialização por percentual (corretagem + agenciamento),
 * compartilhados entre PME e Capital Global.
 *
 * Apresentacional: a validação e o estado do form ficam com o produto — aqui só
 * entram a máscara pt-BR e a exibição dos erros. O campo NUNCA altera o valor
 * digitado (os tetos são mensagem de erro, não clamp).
 */
export function CommercializationPercentFields({
  brokerageValue,
  agencingValue,
  onBrokerageChange,
  onAgencingChange,
  brokerageError,
  agencingError,
  limitError,
  disabled = false,
  autoFocusBrokerage = false,
}: Props) {
  const overLimit = Boolean(limitError);

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <Field.Root invalid={Boolean(brokerageError) || overLimit}>
          <Field.Label>
            Percentual da Corretagem
            <span className='text-alba-error-red-500'>*</span>
          </Field.Label>
          <PercentInput
            value={brokerageValue}
            onChange={onBrokerageChange}
            disabled={disabled}
            autoFocus={autoFocusBrokerage}
          />
          {brokerageError && (
            <Field.ErrorText>{brokerageError}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root invalid={Boolean(agencingError) || overLimit}>
          <Field.Label>Percentual do Agenciamento</Field.Label>
          <PercentInput
            value={agencingValue}
            onChange={onAgencingChange}
            disabled={disabled}
          />
          {agencingError && <Field.ErrorText>{agencingError}</Field.ErrorText>}
        </Field.Root>
      </div>

      {limitError && (
        <Text className='text-sm text-alba-error-red-500'>{limitError}</Text>
      )}
    </div>
  );
}

/**
 * Campo de percentual com o `%` como addon do `InputGroup` (mesmo padrão do
 * design system), em vez de sufixo no placeholder.
 *
 * A borda do input é zerada porque quem desenha a moldura passa a ser o grupo —
 * inclusive o estado de erro: o `InputGroup` tem
 * `has-[[data-slot=input-group-control][aria-invalid=true]]:border-alba-error-red-*`,
 * então o `data-slot` abaixo é o que faz o `Field.Root invalid` continuar
 * pintando a borda de vermelho.
 */
function PercentInput({
  value,
  onChange,
  disabled,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <InputGroup>
      <Field.Input
        className='!border-0 !bg-transparent'
        data-slot='input-group-control'
        value={value}
        onChange={e => onChange(formatPercentInput(e.target.value))}
        placeholder='0,00'
        disabled={disabled}
        autoFocus={autoFocus}
      />
      <InputGroupAddon align='inline-end'>
        <InputGroupText>%</InputGroupText>
      </InputGroupAddon>
    </InputGroup>
  );
}
